import React, { useState, useEffect, useRef } from 'react';
import { Search, User, Plus, Users, UserCheck, UsersRound } from 'lucide-react';
import { useMessaging } from '../contexts/MessagingContext';
import { useClientMessaging } from '../contexts/ClientMessagingContext';
import { collection, getDocs, query, where } from 'firebase/firestore';
import EnhancedModal, { ModalHeader, ModalBody, ModalFooter, ModalButton } from './EnhancedModal';
import { useTheme } from '../contexts/ThemeContext';

const NewConversationModal = ({ onClose, currentUser, db, eligibleIntervenants = [], isClientView = false }) => {
  // Utiliser le bon contexte selon la vue
  const messagingContext = isClientView ? useClientMessaging() : useMessaging();
  const { startConversation, createGroup } = messagingContext;
  const { theme } = useTheme();

  // Détecter le mode dark en vérifiant la classe HTML plutôt que la valeur du thème
  // Cela fonctionne même quand theme === 'system'
  const isDarkMode = document.documentElement.classList.contains('dark');

  const [searchTerm, setSearchTerm] = useState('');
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState('intervenants'); // 'intervenants', 'beneficiaires', ou 'groupe'
  const [showGroupCreation, setShowGroupCreation] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const searchInputRef = useRef(null);

  // Récupérer les intervenants et bénéficiaires depuis Firestore avec les noms des rôles
  useEffect(() => {
    const fetchAvailableUsers = async () => {
      setLoading(true);
      try {
        let users = [];

        // Si c'est une vue bénéficiaire et qu'on a des intervenants éligibles, utiliser ceux-ci
        // pour l'onglet intervenants ET pour l'onglet groupe
        if (isClientView && eligibleIntervenants.length > 0 && (activeTab === 'intervenants' || activeTab === 'groupe')) {
          console.log('🔍 Utilisation des intervenants éligibles pour bénéficiaire:', eligibleIntervenants.length, 'onglet:', activeTab);
          users = eligibleIntervenants.map(intervenant => ({
            ...intervenant,
            type: 'intervenant',
            isActive: true
          }));
        } else {
          // Récupérer tous les rôles depuis advanced_roles
          const rolesSnapshot = await getDocs(collection(db, 'advanced_roles'));
          const rolesMap = new Map();
          rolesSnapshot.forEach(doc => {
            const roleData = doc.data();
            rolesMap.set(doc.id, roleData.name || doc.id);
          });

          if (activeTab === 'intervenants' || activeTab === 'groupe') {
            // Récupérer les intervenants (employés)
            const employeesQuery = query(
              collection(db, 'employees')
              // Supprimer le filtre isActive pour récupérer tous les employés
            );
            const employeesSnapshot = await getDocs(employeesQuery);

            console.log('Employés récupérés:', employeesSnapshot.size);

            // Récupérer tous les IDs des employés qui sont des bénéficiaires-utilisateurs
            // Identifiés par leur email qui commence par "client_"
            // Et extraire l'ID client de leur email (format: client_{clientId}@agentime.fr)
            const beneficiaireUserMap = new Map(); // Map<clientId, employeeId>
            employeesSnapshot.forEach(doc => {
              const employeeData = doc.data();
              if (employeeData.email && employeeData.email.startsWith('client_')) {
                // Extraire l'ID client depuis l'email: client_e4eqcgmsooilgnrkxol5@agentime.fr -> e4eqcgmsooilgnrkxol5
                const clientId = employeeData.email.replace('client_', '').split('@')[0];
                console.log('✅ Bénéficiaire-Utilisateur détecté:', doc.id, employeeData.email, '-> clientId:', clientId);
                beneficiaireUserMap.set(clientId.toLowerCase(), doc.id); // Stocker en minuscules pour comparaison
              }
            });
            console.log('📊 Total bénéficiaires-utilisateurs détectés:', beneficiaireUserMap.size);

            employeesSnapshot.forEach(doc => {
              const employeeData = doc.data();
              console.log('Employé:', doc.id, employeeData);

              // Exclure les comptes bénéficiaires (email commence par "client_")
              // Ces utilisateurs seront ajoutés depuis la table clients
              if (employeeData.email && employeeData.email.startsWith('client_')) {
                console.log('Employé-Bénéficiaire exclu (sera ajouté depuis clients):', doc.id, employeeData.email);
                return;
              }

              // Récupérer le nom du rôle
              const roleId = employeeData.role;
              const roleName = rolesMap.get(roleId) || roleId || 'Collaborateur';

              // Inclure tous les employés normaux (intervenants)
              users.push({
                id: doc.id,
                firstName: employeeData.firstName || '',
                lastName: employeeData.lastName || '',
                name: `${employeeData.firstName || ''} ${employeeData.lastName || ''}`.trim() || 'Utilisateur sans nom',
                role: roleName,
                roleId: roleId,
                type: 'intervenant',
                email: employeeData.email || '',
                isActive: employeeData.isActive !== false // Par défaut actif si non défini
              });
            });

            // Pour l'onglet groupe, récupérer SEULEMENT les bénéficiaires qui ont un compte utilisateur
            if (activeTab === 'groupe' && beneficiaireUserMap.size > 0) {
              console.log('🔍 Recherche des bénéficiaires-utilisateurs dans la table clients...');
              const clientsQuery = query(collection(db, 'clients'));
              const clientsSnapshot = await getDocs(clientsQuery);

              console.log('Clients récupérés pour groupe:', clientsSnapshot.size);

              let beneficiairesAdded = 0;
              clientsSnapshot.forEach(doc => {
                const clientData = doc.data();

                // Utiliser l'ID client (doc.id) en minuscules pour correspondre avec beneficiaireUserMap
                const clientIdLower = doc.id.toLowerCase();

                // Ajouter SEULEMENT les bénéficiaires qui ont un compte utilisateur
                if (beneficiaireUserMap.has(clientIdLower)) {
                  const employeeId = beneficiaireUserMap.get(clientIdLower);
                  console.log('✅ Bénéficiaire-Utilisateur ajouté depuis clients:', doc.id, '(Employee ID:', employeeId, ')', clientData);
                  users.push({
                    id: doc.id, // Utiliser l'ID client pour l'envoi de messages
                    firstName: clientData.firstName || '',
                    lastName: clientData.lastName || '',
                    name: `${clientData.firstName || ''} ${clientData.lastName || ''}`.trim() || clientData.name || 'Bénéficiaire',
                    role: 'Bénéficiaire (Utilisateur)',
                    type: 'beneficiaire',
                    email: clientData.email || '',
                    isActive: clientData.isActive !== false,
                    hasUserAccount: true
                  });
                  beneficiairesAdded++;
                } else {
                  console.log('❌ Client ignoré (pas de compte utilisateur):', doc.id);
                }
              });
              console.log(`📊 Total bénéficiaires-utilisateurs ajoutés: ${beneficiairesAdded}`);
            } else if (activeTab === 'groupe') {
              console.log('⚠️ Aucun bénéficiaire-utilisateur détecté dans employees');
            }
          } else if (activeTab === 'beneficiaires') {
            // Pour l'onglet bénéficiaires, récupérer SEULEMENT les bénéficiaires qui ont un compte utilisateur
            // D'abord récupérer les employés pour identifier les bénéficiaires-utilisateurs
            const employeesQuery = query(collection(db, 'employees'));
            const employeesSnapshot = await getDocs(employeesQuery);

            // Créer la Map des bénéficiaires-utilisateurs (clientId -> employeeId)
            const beneficiaireUserMap = new Map();
            employeesSnapshot.forEach(doc => {
              const employeeData = doc.data();
              if (employeeData.email && employeeData.email.startsWith('client_')) {
                // Extraire l'ID client depuis l'email: client_e4eqcgmsooilgnrkxol5@agentime.fr -> e4eqcgmsooilgnrkxol5
                const clientId = employeeData.email.replace('client_', '').split('@')[0];
                console.log('✅ Bénéficiaire-Utilisateur détecté (tab bénéficiaires):', doc.id, employeeData.email, '-> clientId:', clientId);
                beneficiaireUserMap.set(clientId.toLowerCase(), doc.id);
              }
            });
            console.log('📊 Total bénéficiaires-utilisateurs détectés (tab bénéficiaires):', beneficiaireUserMap.size);

            // Ensuite récupérer les clients et filtrer ceux qui ont un compte utilisateur
            const clientsQuery = query(collection(db, 'clients'));
            const clientsSnapshot = await getDocs(clientsQuery);

            console.log('Clients récupérés (tab bénéficiaires):', clientsSnapshot.size);

            let beneficiairesAdded = 0;
            clientsSnapshot.forEach(doc => {
              const clientData = doc.data();
              const clientIdLower = doc.id.toLowerCase();

              // Ajouter SEULEMENT les bénéficiaires qui ont un compte utilisateur
              if (beneficiaireUserMap.has(clientIdLower)) {
                const employeeId = beneficiaireUserMap.get(clientIdLower);
                console.log('✅ Bénéficiaire-Utilisateur ajouté (tab bénéficiaires):', doc.id, '(Employee ID:', employeeId, ')', clientData);
                users.push({
                  id: doc.id,
                  firstName: clientData.firstName || '',
                  lastName: clientData.lastName || '',
                  name: `${clientData.firstName || ''} ${clientData.lastName || ''}`.trim() || clientData.name || 'Bénéficiaire',
                  role: 'Bénéficiaire (Utilisateur)',
                  type: 'beneficiaire',
                  email: clientData.email || '',
                  isActive: clientData.isActive !== false,
                  hasUserAccount: true
                });
                beneficiairesAdded++;
              } else {
                console.log('❌ Client ignoré (pas de compte utilisateur - tab bénéficiaires):', doc.id);
              }
            });
            console.log(`📊 Total bénéficiaires-utilisateurs ajoutés (tab bénéficiaires): ${beneficiairesAdded}`);
          }
        }

        console.log('Total utilisateurs récupérés:', users.length);
        setAvailableUsers(users);
      } catch (error) {
        console.error('Erreur récupération utilisateurs:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailableUsers();
  }, [db, currentUser?.id, activeTab]);

  const filteredUsers = availableUsers.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase());
    const isNotCurrentUser = user.id !== currentUser?.id;
    
    console.log('Filtrage utilisateur:', user.name, 'matchesSearch:', matchesSearch, 'isNotCurrentUser:', isNotCurrentUser);
    
    return matchesSearch && isNotCurrentUser;
  });

  const toggleParticipant = (userId) => {
    setSelectedParticipants(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );

    // Refocuser la barre de recherche pour permettre de continuer à ajouter des participants
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 0);
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    
    if (!groupName.trim()) {
      alert('Le nom du groupe est obligatoire');
      return;
    }

    if (selectedParticipants.length < 1) {
      alert('Sélectionnez au moins un participant');
      return;
    }

    setCreatingGroup(true);
    try {
      await createGroup(groupName.trim(), groupDescription.trim(), selectedParticipants);
      onClose();
    } catch (error) {
      console.error('Erreur création groupe:', error);
      alert('Impossible de créer le groupe. Veuillez réessayer.');
    } finally {
      setCreatingGroup(false);
    }
  };


  const handleStartConversation = async (userId, userType) => {
    try {
      // Déterminer le type de conversation selon le contexte
      // Si c'est une vue client, c'est toujours une conversation 'client'
      // Sinon, déterminer selon l'onglet actif
      const conversationType = isClientView
        ? 'client'
        : activeTab === 'beneficiaires'
          ? 'client'
          : 'internal';

      console.log('🔵 Création conversation:', { userId, conversationType, isClientView, activeTab });
      await startConversation(userId, conversationType);
      onClose();
    } catch (error) {
      console.error('Erreur démarrage conversation:', error);
    }
  };

  return (
    <EnhancedModal
      onClose={onClose}
      size="lg"
    >
      <ModalHeader>
        Nouvelle conversation
      </ModalHeader>

      <ModalBody className="space-y-4">
        {/* Onglets Intervenants/Bénéficiaires/Groupe */}
        <div className={`rounded-lg p-1 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <div className="flex space-x-1">
            <button
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'intervenants'
                  ? isDarkMode
                    ? 'bg-gray-700 text-blue-400 shadow-sm'
                    : 'bg-white text-blue-600 shadow-sm'
                  : isDarkMode
                    ? 'text-gray-400 hover:text-gray-200'
                    : 'text-gray-600 hover:text-gray-900'
              }`}
              onClick={() => setActiveTab('intervenants')}
            >
              <Users size={16} />
              <span>Intervenants</span>
            </button>

            {/* Masquer l'onglet Bénéficiaires pour les bénéficiaires */}
            {!isClientView && (
              <button
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === 'beneficiaires'
                    ? isDarkMode
                      ? 'bg-gray-700 text-blue-400 shadow-sm'
                      : 'bg-white text-blue-600 shadow-sm'
                    : isDarkMode
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setActiveTab('beneficiaires')}
              >
                <UserCheck size={16} />
                <span>Bénéficiaires</span>
              </button>
            )}

            {/* Onglet Créer un groupe - disponible pour tous */}
            <button
              className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'groupe'
                  ? isDarkMode
                    ? 'bg-gray-700 text-blue-400 shadow-sm'
                    : 'bg-white text-blue-600 shadow-sm'
                  : isDarkMode
                    ? 'text-gray-400 hover:text-gray-200'
                    : 'text-gray-600 hover:text-gray-900'
              }`}
              onClick={() => setActiveTab('groupe')}
            >
              <UsersRound size={16} />
              <span>Créer un groupe</span>
            </button>
          </div>
        </div>

        {/* Barre de recherche */}
        <div className="relative">
          <Search size={16} className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          <input
            ref={searchInputRef}
            type="text"
            placeholder={
              activeTab === 'intervenants' ? 'Rechercher un intervenant...' :
              activeTab === 'beneficiaires' ? 'Rechercher un bénéficiaire...' :
              'Rechercher un intervenant...'
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 transition-colors ${
              isDarkMode
                ? 'bg-gray-800 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-blue-400 focus:ring-blue-400'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500'
            }`}
          />
        </div>

        {/* Contenu selon l'onglet sélectionné */}
        {activeTab === 'groupe' ? (
          <div className="space-y-4">
            <div>
              <label htmlFor="groupName" className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Nom du groupe <span className="text-red-500">*</span>
              </label>
              <input
                id="groupName"
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder="Ex: Équipe du matin"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${
                  isDarkMode
                    ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500 focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                }`}
                maxLength={50}
                required
              />
            </div>

            <div>
              <label htmlFor="groupDescription" className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Description (optionnelle)
              </label>
              <textarea
                id="groupDescription"
                value={groupDescription}
                onChange={(e) => setGroupDescription(e.target.value)}
                placeholder="Ex: Groupe pour coordonner les interventions du matin"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 transition-colors ${
                  isDarkMode
                    ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500 focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                }`}
                rows={3}
                maxLength={200}
              />
            </div>

            <div>
              <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Participants ({selectedParticipants.length} sélectionné{selectedParticipants.length > 1 ? 's' : ''})
              </h4>

              {/* Liste des participants sélectionnés */}
              {selectedParticipants.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {selectedParticipants.map(participantId => {
                    const participant = availableUsers.find(u => u.id === participantId);
                    return participant ? (
                      <span key={participantId} className={`inline-flex items-center gap-1 px-2 py-1 text-sm rounded-full ${
                        isDarkMode
                          ? 'bg-blue-900 text-blue-200'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {participant.name}
                        <button
                          type="button"
                          onClick={() => toggleParticipant(participantId)}
                          className={isDarkMode ? 'text-blue-300 hover:text-blue-100' : 'text-blue-600 hover:text-blue-800'}
                        >
                          ×
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              )}

              {/* Liste des intervenants disponibles */}
              <div className={`border rounded-lg max-h-40 overflow-y-auto ${
                isDarkMode ? 'border-gray-700' : 'border-gray-200'
              }`}>
                {filteredUsers.map(user => {
                  const isSelected = selectedParticipants.includes(user.id);
                  return (
                    <div
                      key={user.id}
                      className={`flex items-center gap-3 p-3 cursor-pointer border-b last:border-b-0 ${
                        isDarkMode
                          ? `border-gray-700 ${isSelected ? 'bg-blue-900 bg-opacity-30' : 'hover:bg-gray-800'}`
                          : `border-gray-100 ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`
                      }`}
                      onClick={() => toggleParticipant(user.id)}
                    >
                      <div className={`w-5 h-5 border-2 rounded flex items-center justify-center ${
                        isSelected
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : isDarkMode
                            ? 'border-gray-600'
                            : 'border-gray-300'
                      }`}>
                        {isSelected && <Plus size={12} />}
                      </div>
                      <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm">
                        <User size={16} />
                      </div>
                      <div className="flex-1">
                        <div className={`text-sm font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{user.name}</div>
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{user.role}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className={`flex justify-center items-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <div>Chargement {activeTab === 'intervenants' ? 'des intervenants' : 'des bénéficiaires'}...</div>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className={`flex flex-col items-center justify-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <User size={48} className={isDarkMode ? 'text-gray-600 mb-2' : 'text-gray-300 mb-2'} />
                <h3 className={`text-lg font-medium mb-1 ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                  Aucun {activeTab === 'intervenants' ? 'intervenant' : 'bénéficiaire'} trouvé
                </h3>
                <p className="text-sm text-center">
                  {activeTab === 'intervenants'
                    ? 'Aucun intervenant ne correspond à votre recherche.'
                    : 'Aucun bénéficiaire ne correspond à votre recherche.'
                  }
                </p>
              </div>
            ) : (
              filteredUsers.map(user => (
                <div
                  key={user.id}
                  className={`flex items-center gap-3 p-3 cursor-pointer border-b last:border-b-0 ${
                    isDarkMode
                      ? 'border-gray-700 hover:bg-gray-800'
                      : 'border-gray-100 hover:bg-gray-50'
                  }`}
                  onClick={() => handleStartConversation(user.id)}
                >
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white">
                    <User size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium truncate ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>{user.name}</div>
                    <div className={`text-xs font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
                      {user.role}
                    </div>
                    {user.email && (
                      <div className={`text-xs truncate ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {user.email}
                      </div>
                    )}
                  </div>
                  <button className={`p-2 rounded-lg transition-colors ${
                    isDarkMode
                      ? 'text-gray-500 hover:text-blue-400 hover:bg-gray-700'
                      : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                  }`}>
                    <Plus size={16} />
                  </button>
                </div>
              ))
            )}
          </div>
        )}

      </ModalBody>

      <ModalFooter>
        {activeTab === 'groupe' ? (
          <>
            <ModalButton
              variant="secondary"
              onClick={onClose}
            >
              Annuler
            </ModalButton>
            <ModalButton
              variant="primary"
              onClick={handleCreateGroup}
              disabled={creatingGroup || !groupName.trim() || selectedParticipants.length === 0}
            >
              {creatingGroup ? 'Création...' : 'Créer le groupe'}
            </ModalButton>
          </>
        ) : (
          <ModalButton
            variant="secondary"
            onClick={onClose}
          >
            Annuler
          </ModalButton>
        )}
      </ModalFooter>
    </EnhancedModal>
  );
};

export default NewConversationModal;
